import { useEffect, useRef, useState } from "react"
import { useFormContext, useWatch } from "react-hook-form"

import type { SalesOrderLogisticsFormValues } from "../schemas"
import { createFreightLineFormValues, hasFreightLineData } from "../utils"

import { useFreightUnit } from "./useFreightUnit"

import { FreightTerms } from "@/graphql/codegen/graphql"

interface UseFreightLineReturn {
  isRemovalConfirmationOpen: boolean
  handleFreightTermsChange: (freightTerms: FreightTerms | null) => void
  handleFreightRemovalConfirm: () => void
  handleFreightRemovalCancel: () => void
}

export const useFreightLine = (): UseFreightLineReturn => {
  const { control, getValues, setValue } = useFormContext<SalesOrderLogisticsFormValues>()
  const [isRemovalConfirmationOpen, setIsRemovalConfirmationOpen] = useState(false)

  const freightUnit = useFreightUnit()

  // The select writes the new value before calling onChange, so the last confirmed terms are tracked
  // separately to let a cancelled removal restore them.
  const freightTerms = useWatch({ control, name: "freightTerms" })
  const committedFreightTermsRef = useRef(freightTerms)
  useEffect(() => {
    if (!isRemovalConfirmationOpen) committedFreightTermsRef.current = freightTerms
  }, [freightTerms, isRemovalConfirmationOpen])

  const handleFreightTermsChange = (nextFreightTerms: FreightTerms | null) => {
    const freightLine = getValues("freightLine")

    if (nextFreightTerms === FreightTerms.PrepaidAndAdd) {
      if (!freightLine) {
        setValue("freightLine", createFreightLineFormValues(freightUnit?.id ?? null), {
          shouldDirty: true,
          shouldValidate: true,
        })
      }
      return
    }

    if (!freightLine) return

    if (hasFreightLineData(freightLine)) {
      setIsRemovalConfirmationOpen(true)
      return
    }

    setValue("freightLine", null, { shouldDirty: true, shouldValidate: true })
  }

  const handleFreightRemovalConfirm = () => {
    setValue("freightLine", null, { shouldDirty: true, shouldValidate: true })
    setIsRemovalConfirmationOpen(false)
  }

  const handleFreightRemovalCancel = () => {
    setValue("freightTerms", committedFreightTermsRef.current, { shouldDirty: true, shouldValidate: true })
    setIsRemovalConfirmationOpen(false)
  }

  return {
    isRemovalConfirmationOpen,
    handleFreightTermsChange,
    handleFreightRemovalConfirm,
    handleFreightRemovalCancel,
  }
}
